/**
 * Post-boot financial domain tasks (Wave 1A), hooked by
 * BootManager once app is `ready` with session:
 *
 *   1. Recurring engine (`runRecurringRules`): materializes due txs
 *      (multi-occurrence catch-up, deterministic UUIDv5 ids) and advances
 *      pointers. Re-executes on returning to foreground (visibilitychange) to
 *      cover apps left open for days; when nothing is due it is a cheap
 *      no-op (indexed query by user+active+next_run_on).
 *   2. Budget alerts (`checkAndNotifyBudgetAlerts`): 80% / 100% with
 *      daily deduplication by budget+period+threshold in localStorage.
 *
 * Integration decision: `appBootMachine` (XState) is left untouched — it is
 * shared and has its own tests; these tasks are side effects without
 * intermediate states justifying a machine step, so they live in a
 * Solid hook that observes `ready` + session + drizzle.
 */
import { type Accessor, createEffect, onCleanup, onMount } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import type { PowerSyncDrizzleDB } from "@cashsync/powersync";
import { runRecurringRules } from "@cashsync/shared";

import { refreshAppConfig } from "@/lib/app-config";
import { checkAndNotifyBudgetAlerts } from "@/lib/notifications/budget-alerts";

interface PostBootFinanceTasksArgs {
  /** Boot machine in `ready`/`redirecting` (visible content). */
  isReady: Accessor<boolean>;
  userId: Accessor<string | undefined>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  /** useI18n translator (budget notification bodies). */
  t: (message: MessageDescriptor, values?: Record<string, unknown>) => string;
  locale?: Accessor<string>;
}

export function usePostBootFinanceTasks(args: PostBootFinanceTasksArgs): void {
  let running = false;

  const run = async (): Promise<void> => {
    const db = args.drizzle();
    const uid = args.userId();
    if (!db || !uid || running || !args.isReady()) {
      return;
    }
    running = true;
    try {
      // CONFIG_OVER_CODE: refreshes operational config BEFORE running
      // tasks. `refreshAppConfig` never throws: if offline, `appConfig()`
      // returns last known value (or code defaults) and tasks
      // run anyway. Local-first: app does not depend on server to
      // materialize a recurring tx or alert about a budget.
      const config = await refreshAppConfig();
      const created = await runRecurringRules(
        db,
        uid,
        undefined,
        config.finance.recurringMaxCatchupOccurrences,
      );
      if (created > 0) {
        console.log(`[post-boot] recurrentes materializadas: ${created}`);
      }
      await checkAndNotifyBudgetAlerts(db, uid, args.t, args.locale?.());
    } catch (err: unknown) {
      console.warn("[post-boot] finance tasks:", err instanceof Error ? err.message : err);
    } finally {
      running = false;
    }
  };

  // Authenticated startup: first pass when boot+session+drizzle are ready.
  createEffect(() => {
    if (args.isReady() && args.userId() && args.drizzle()) {
      void run();
    }
  });

  // Foreground return: catch-up if tab/window spent days in background.
  onMount(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void run();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    onCleanup(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    });
  });
}
